import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { posAPI, authAPI } from '../../services/api';
import { FaRupeeSign, FaHistory, FaCreditCard, FaSignOutAlt, FaStore, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';

export default function POSDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    posAPI.dashboard()
      .then(r => setData(r.data?.data || r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = async () => {
    try { await authAPI.logout(); } catch (_) {}
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div style={S.page}>
      {/* Top Nav */}
      <nav style={S.nav}>
        <div style={S.navBrand}><FaStore style={{ marginRight: 8 }} />A1xPay POS</div>
        <div style={S.navRight}>
          <span style={S.navUser}>{user?.email}</span>
          <button onClick={handleLogout} style={S.navBtn}><FaSignOutAlt /></button>
        </div>
      </nav>

      <div style={S.body}>
        <h1 style={S.heading}>POS Dashboard</h1>
        <p style={S.sub}>Welcome back! Use your POS terminal to collect payments.</p>

        {/* Status Banner */}
        {data?.terminal ? (
          <div style={{ ...S.banner, background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
            <FaCheckCircle style={{ color: '#16A34A', marginRight: 8 }} />
            <span style={{ color: '#15803D', fontWeight: 600 }}>Terminal Active</span>
            <span style={{ color: '#6e6e86', marginLeft: 12, fontSize: 13 }}>
              Store: {data.terminal.store_name} &nbsp;|&nbsp; Terminal ID: {data.terminal.terminal_id}
            </span>
          </div>
        ) : !loading && (
          <div style={{ ...S.banner, background: '#FFFBEB', border: '1px solid #FDE68A' }}>
            <FaExclamationCircle style={{ color: '#D97706', marginRight: 8 }} />
            <span style={{ color: '#B45309', fontWeight: 600 }}>No terminal assigned yet.</span>
            <span style={{ color: '#6e6e86', marginLeft: 8, fontSize: 13 }}>Contact admin to assign your POS terminal.</span>
          </div>
        )}

        {/* Stats */}
        <div style={S.statsRow}>
          <StatCard icon={<FaRupeeSign />} label="Today's Collection" value={loading ? '…' : `₹${(data?.today_amount || 0).toLocaleString('en-IN')}`} color="#7c3aed" />
          <StatCard icon={<FaHistory />}  label="Today's Transactions" value={loading ? '…' : (data?.today_count || 0)} color="#7c3aed" />
          <StatCard icon={<FaRupeeSign />} label="Total Collection" value={loading ? '…' : `₹${(data?.total_amount || 0).toLocaleString('en-IN')}`} color="#059669" />
        </div>

        {/* Action Buttons */}
        <div style={S.actionRow}>
          <ActionCard
            icon={<FaCreditCard size={28} />}
            title="Collect Payment"
            desc="Enter amount and initiate payment on POS machine"
            color="#14142b"
            onClick={() => navigate('/pos/collect')}
          />
          <ActionCard
            icon={<FaHistory size={28} />}
            title="Transaction History"
            desc="View all past transactions and their status"
            color="#7c3aed"
            onClick={() => navigate('/pos/transactions')}
          />
        </div>

        {/* Recent Transactions */}
        {data?.recent_transactions?.length > 0 && (
          <div style={S.recentBox}>
            <div style={S.recentTitle}>Recent Transactions</div>
            <table style={S.table}>
              <thead>
                <tr>
                  {['ID', 'Amount', 'Status', 'Time'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.recent_transactions.map(t => (
                  <tr key={t.id} style={S.tr}>
                    <td style={S.td}>{t.a1x_transaction_id}</td>
                    <td style={S.td}>₹{(t.amount || 0).toLocaleString('en-IN')}</td>
                    <td style={S.td}><StatusBadge status={t.status} /></td>
                    <td style={S.td}>{new Date(t.created_at).toLocaleString('en-IN', { hour12: true, hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ icon, label, value, color }) => (
  <div style={S.statCard}>
    <div style={{ ...S.statIcon, color }}>{icon}</div>
    <div style={S.statVal}>{value}</div>
    <div style={S.statLabel}>{label}</div>
  </div>
);

const ActionCard = ({ icon, title, desc, color, onClick }) => (
  <button onClick={onClick} style={{ ...S.actionCard, borderTop: `4px solid ${color}` }}>
    <div style={{ color, marginBottom: 12 }}>{icon}</div>
    <div style={S.actionTitle}>{title}</div>
    <div style={S.actionDesc}>{desc}</div>
  </button>
);

const StatusBadge = ({ status }) => {
  const map = {
    success:   { bg: '#F0FDF4', color: '#16A34A', label: 'Success' },
    failed:    { bg: '#FEF2F2', color: '#DC2626', label: 'Failed' },
    pending:   { bg: '#FFFBEB', color: '#D97706', label: 'Pending' },
    initiated: { bg: '#f1ebff', color: '#7c3aed', label: 'Initiated' },
  };
  const s = map[status] || map.pending;
  return <span style={{ background: s.bg, color: s.color, padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{s.label}</span>;
};

const S = {
  page:  { minHeight: '100vh', background: '#e6e6f0', fontFamily: '"Plus Jakarta Sans", "Plus Jakarta Sans", system-ui, Arial, sans-serif' },
  nav:   { background: '#14142b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px' },
  navBrand: { display: 'flex', alignItems: 'center', fontWeight: 700, fontSize: 18 },
  navRight: { display: 'flex', alignItems: 'center', gap: 12 },
  navUser:  { fontSize: 13, color: '#6e6e86' },
  navBtn:   { background: 'none', border: '1px solid #334155', color: '#6e6e86', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' },
  body:  { maxWidth: 900, margin: '0 auto', padding: '32px 16px' },
  heading: { fontSize: 26, fontWeight: 700, color: '#14142b', margin: '0 0 6px' },
  sub:     { fontSize: 14, color: '#4a4a6a', marginBottom: 24 },
  banner:  { display: 'flex', alignItems: 'center', padding: '12px 16px', borderRadius: 10, marginBottom: 24, fontSize: 14 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 },
  statCard:  { background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.07)', textAlign: 'center' },
  statIcon:  { fontSize: 22, marginBottom: 10 },
  statVal:   { fontSize: 26, fontWeight: 700, color: '#14142b', marginBottom: 4 },
  statLabel: { fontSize: 12, color: '#4a4a6a' },
  actionRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 32 },
  actionCard: { background: '#fff', borderRadius: 14, padding: 28, boxShadow: '0 1px 4px rgba(0,0,0,.07)', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'box-shadow .2s' },
  actionTitle: { fontWeight: 700, fontSize: 16, color: '#14142b', marginBottom: 6 },
  actionDesc:  { fontSize: 13, color: '#4a4a6a', lineHeight: 1.5 },
  recentBox:   { background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.07)' },
  recentTitle: { fontWeight: 700, fontSize: 16, color: '#14142b', marginBottom: 16 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#4a4a6a', padding: '8px 12px', borderBottom: '1px solid #e8e8f1', textTransform: 'uppercase' },
  td: { padding: '10px 12px', fontSize: 13, color: '#14142b', borderBottom: '1px solid #f0f0f6' },
  tr: { transition: 'background .15s' },
};
